import { c as _c } from "react/compiler-runtime";
import { addressesRegistry, type ChainId, defaultPreLiquidationParamsRegistry, type MarketId, MarketParams } from '@morpho-org/blue-sdk';
import { blueAbi } from '@morpho-org/blue-sdk-viem';
import { InterfaceChainUtils, ZERO } from '@morpho-org/dapp-core';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useReadContract } from 'wagmi';
import { useWeb3Context } from '@/contexts/Web3Context';
import { computePreLiquidationAddress } from '../utils/computePreLiquidationAddress';
import { preLiquidationFactoryContractAbi } from '../utils/preLiquidationFactoryContractAbi';
type Status = 'loading' | 'error' | 'hidden' | 'unauthorized' | 'authorized';

/**
 * Hook that manages auto-deleverage (pre-liquidation) state for a market.
 * Verifies contract deployment and user authorization status.
 * @param marketId - Market identifier to check auto-deleverage for
 * @param chainId - Chain ID of the market. Callers must pass this explicitly to
 *   avoid silently reading from the wrong chain in multi-chain contexts (e.g.
 *   the Dashboard table).
 * @returns Object with status, refetch functions, preLltv, and contract address
 */
export const useAutoDeleverage = (marketId, chainId) => {
  const $ = _c(61);
  const {
    account: t0
  } = useWeb3Context();
  const {
    address: walletAddress
  } = t0;
  const queryClient = useQueryClient();
  let t1;
  bb0: {
    if (!InterfaceChainUtils.isPreLiquidationSupported(chainId)) {
      t1 = undefined;
      break bb0;
    }
    t1 = addressesRegistry[chainId].preLiquidationFactory;
  }
  const preLiquidationFactory = t1;
  const t2 = !!marketId;
  let t3;
  if ($[0] !== t2) {
    t3 = {
      enabled: t2,
      staleTime: Infinity
    };
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const t4 = addressesRegistry[chainId];
  let t5;
  if ($[2] !== marketId) {
    t5 = [marketId];
    $[2] = marketId;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== chainId || $[5] !== t3 || $[6] !== t4.blue || $[7] !== t5) {
    t6 = {
      query: t3,
      chainId,
      abi: blueAbi,
      address: t4.blue,
      functionName: "idToMarketParams",
      args: t5
    };
    $[4] = chainId;
    $[5] = t3;
    $[6] = t4.blue;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const {
    data: t7,
    error: marketParamsError,
    isLoading: isMarketParamsLoading,
    refetch: refetchMarketParams
  } = useReadContract(t6);
  let t8;
  if ($[9] !== t7) {
    t8 = t7 === undefined ? [] : t7;
    $[9] = t7;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const [loanToken, collateralToken, oracleAddress, irm, lltv] = t8;
  let t9;
  bb1: {
    if (loanToken == null || collateralToken == null || oracleAddress == null || irm == null || lltv == null) {
      t9 = undefined;
      break bb1;
    }
    let t10;
    if ($[11] !== collateralToken || $[12] !== irm || $[13] !== lltv || $[14] !== loanToken || $[15] !== oracleAddress) {
      t10 = new MarketParams({
        loanToken,
        collateralToken,
        oracle: oracleAddress,
        irm,
        lltv
      });
      $[11] = collateralToken;
      $[12] = irm;
      $[13] = lltv;
      $[14] = loanToken;
      $[15] = oracleAddress;
      $[16] = t10;
    } else {
      t10 = $[16];
    }
    t9 = t10;
  }
  const marketParams = t9;
  let t10;
  if ($[17] !== lltv) {
    t10 = defaultPreLiquidationParamsRegistry.get(lltv ?? ZERO);
    $[17] = lltv;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const params = t10;
  let t11;
  bb2: {
    if (!params || !oracleAddress || !marketId || !preLiquidationFactory) {
      t11 = null;
      break bb2;
    }
    const t12 = addressesRegistry[chainId];
    let t13;
    if ($[19] !== chainId || $[20] !== marketId || $[21] !== oracleAddress || $[22] !== params || $[23] !== preLiquidationFactory || $[24] !== t12.blue) {
      t13 = computePreLiquidationAddress({
        chainId,
        morpho: t12.blue,
        preLiquidationFactory,
        marketId,
        preLiquidationParams: {
          ...params,
          preLiquidationOracle: oracleAddress
        }
      });
      $[19] = chainId;
      $[20] = marketId;
      $[21] = oracleAddress;
      $[22] = params;
      $[23] = preLiquidationFactory;
      $[24] = t12.blue;
      $[25] = t13;
    } else {
      t13 = $[25];
    }
    t11 = t13;
  }
  const preLiquidationContractAddress = t11;
  const t12 = !!preLiquidationContractAddress && !!preLiquidationFactory;
  let t13;
  if ($[26] !== t12) {
    t13 = {
      enabled: t12,
      staleTime: Infinity
    };
    $[26] = t12;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== preLiquidationContractAddress) {
    t14 = [preLiquidationContractAddress];
    $[28] = preLiquidationContractAddress;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== chainId || $[31] !== preLiquidationFactory || $[32] !== t13 || $[33] !== t14) {
    t15 = {
      query: t13,
      chainId,
      abi: preLiquidationFactoryContractAbi,
      address: preLiquidationFactory,
      functionName: "isPreLiquidation",
      args: t14
    };
    $[30] = chainId;
    $[31] = preLiquidationFactory;
    $[32] = t13;
    $[33] = t14;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  const {
    data: isPreLiquidation,
    isLoading: isPreLiquidationLoading,
    isError: isPreLiquidationError,
    queryKey: isPreLiquidationQueryKey
  } = useReadContract(t15);
  const t16 = !!walletAddress && !!preLiquidationContractAddress;
  let t17;
  if ($[35] !== t16) {
    t17 = {
      enabled: t16,
      staleTime: Infinity
    };
    $[35] = t16;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  const t18 = addressesRegistry[chainId];
  let t19;
  if ($[37] !== preLiquidationContractAddress || $[38] !== walletAddress) {
    t19 = [walletAddress, preLiquidationContractAddress];
    $[37] = preLiquidationContractAddress;
    $[38] = walletAddress;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  let t20;
  if ($[40] !== chainId || $[41] !== t17 || $[42] !== t18.blue || $[43] !== t19) {
    t20 = {
      query: t17,
      chainId,
      abi: blueAbi,
      address: t18.blue,
      functionName: "isAuthorized",
      args: t19
    };
    $[40] = chainId;
    $[41] = t17;
    $[42] = t18.blue;
    $[43] = t19;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  const {
    data: isAuthorized,
    isLoading: isAuthorizedLoading,
    isError: isAuthorizedError,
    queryKey: isAuthorizedQueryKey
  } = useReadContract(t20);
  let t21;
  bb3: {
    if (!walletAddress) {
      t21 = "hidden";
      break bb3;
    }
    if (isMarketParamsLoading || isPreLiquidationLoading || isAuthorizedLoading) {
      t21 = "loading";
      break bb3;
    }
    if (isPreLiquidation === false) {
      t21 = "hidden";
      break bb3;
    }
    if (isAuthorized === false) {
      t21 = "unauthorized";
      break bb3;
    }
    if (isAuthorized === true) {
      t21 = "authorized";
      break bb3;
    }
    if (marketParamsError || isPreLiquidationError || isAuthorizedError) {
      t21 = "error";
      break bb3;
    }
    t21 = "hidden";
  }
  const status = t21;
  let t22;
  if ($[45] !== isAuthorizedQueryKey || $[46] !== isPreLiquidationQueryKey || $[47] !== queryClient || $[48] !== refetchMarketParams) {
    t22 = () => {
      refetchMarketParams();
      queryClient.invalidateQueries({
        queryKey: isPreLiquidationQueryKey
      });
      queryClient.invalidateQueries({
        queryKey: isAuthorizedQueryKey
      });
    };
    $[45] = isAuthorizedQueryKey;
    $[46] = isPreLiquidationQueryKey;
    $[47] = queryClient;
    $[48] = refetchMarketParams;
    $[49] = t22;
  } else {
    t22 = $[49];
  }
  const refetchAll = t22;
  let t23;
  if ($[50] !== isAuthorizedQueryKey || $[51] !== queryClient) {
    t23 = () => queryClient.invalidateQueries({
      queryKey: isAuthorizedQueryKey
    });
    $[50] = isAuthorizedQueryKey;
    $[51] = queryClient;
    $[52] = t23;
  } else {
    t23 = $[52];
  }
  const t24 = params?.preLltv;
  let t25;
  if ($[53] !== isPreLiquidation || $[54] !== marketParams || $[55] !== preLiquidationContractAddress || $[56] !== refetchAll || $[57] !== status || $[58] !== t23 || $[59] !== t24) {
    t25 = {
      status,
      refetchAll,
      refetchAuthorizationQuery: t23,
      preLltv: t24,
      preLiquidationContractAddress,
      isPreLiquidation,
      marketParams
    };
    $[53] = isPreLiquidation;
    $[54] = marketParams;
    $[55] = preLiquidationContractAddress;
    $[56] = refetchAll;
    $[57] = status;
    $[58] = t23;
    $[59] = t24;
    $[60] = t25;
  } else {
    t25 = $[60];
  }
  return t25;
};